import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

import useProductAnalytics from '@/analytics/hooks/useProductAnalytics';
import type {
  RingPDPRoutePrefix,
  UseRingScroller,
} from '@/app/[locale]/store/_components/HardwarePDP/_hooks/types';
import useBuildRingOrder from '@/app/[locale]/store/_components/HardwarePDP/_hooks/useBuildRingOrder';
import useCurrentRoute, {
  type CurrentRouteValues,
} from '@/app/[locale]/store/_components/HardwarePDP/_hooks/useCurrentRoute';
import useRingPDPProductsData from '@/app/[locale]/store/_components/HardwarePDP/_hooks/useRingPDPProductsData';
import useRingScroller from '@/app/[locale]/store/_components/HardwarePDP/_hooks/useRingScroller';
import type { BuildHardwareOrderBaseProps } from '@/app/[locale]/store/_components/HardwarePDP/_types/order';
import {
  EXTENDED_WARRANTY_KEYS,
  MEMBERSHIP_1YR_KEY,
} from '@/app/consts/productKeys';
import useCurrency from '@/app/hooks/useCurrency';
import { useProductsData } from '@/queries/Products';
import type { ListProduct } from '@/queries/utils/mapProductFragmentToListProduct';

export interface UseRingPDPPageProps {
  isAddingToCart: boolean;
  locale: string;
  productKeys: string[];
  ringHandlePrefix: string;
  routePrefix: RingPDPRoutePrefix;
  sessionStorageKey: string;
}

export interface UseRingPDPPageState extends BuildHardwareOrderBaseProps {
  currencyCode: string;
  currentRoute: CurrentRouteValues;
  onFinishChange: (value: string) => void;
  productsData: ListProduct[] | undefined;
  productsQueryError: unknown;
}

const useRingPDPPage = ({
  isAddingToCart,
  locale,
  productKeys,
  ringHandlePrefix,
  routePrefix,
  sessionStorageKey,
}: UseRingPDPPageProps): UseRingPDPPageState & UseRingScroller => {
  const pathname = usePathname();

  const { currencyCode } = useCurrency();

  const { error: productsQueryError, data: productsData } = useProductsData({
    includeInventory: true,
    productKeys: [
      ...productKeys,
      ...EXTENDED_WARRANTY_KEYS,
      MEMBERSHIP_1YR_KEY,
    ],
  });
  const currentRoute = useCurrentRoute({
    locale,
    routePrefix,
  });
  const { focusWithinProps, sectionRefs } = useRingScroller();
  const { sendProductViewedEvent } = useProductAnalytics();
  const productViewedPath = useRef<string>('');

  const {
    product,
    oneYearMembership: yearlyMembership,
    ringSizesData,
    subscription: monthlyMembership,
    threeYearWarranty,
    twoYearWarranty,
    ...rest
  } = useRingPDPProductsData({
    currentRoute,
    productsData,
    ringHandlePrefix,
  });

  const {
    isSelectingSize,
    onFinishChange,
    onRadioChange,
    onSizeChange,
    order,
  } = useBuildRingOrder({
    currentRoute,
    isAddingToCart,
    ringSizesData,
    sessionStorageKey,
    ...rest,
  });

  // Handle product viewed event
  useEffect(() => {
    // Prevent sending the event for the same product multiple times
    if (productViewedPath.current !== pathname && product) {
      productViewedPath.current = pathname;
      void sendProductViewedEvent({
        path: pathname,
        product,
      });
    }
  }, [pathname, product, sendProductViewedEvent]);

  return {
    currencyCode,
    currentRoute,
    focusWithinProps,
    isSelectingSize,
    monthlyMembership,
    onFinishChange,
    onRadioChange,
    onSizeChange,
    order,
    product,
    productsData,
    productsQueryError,
    ringSizesData,
    sectionRefs,
    threeYearWarranty,
    twoYearWarranty,
    yearlyMembership,
  };
};

export default useRingPDPPage;
